/*
 * Project: BookingEngine
 * Module: components/booking/rooms/RoomHighlights.js
 * vim:ft=javascript.jsx:
 */
import React from 'react';
import PropTypes from 'prop-types';

import BedIcon from '../../icons/highlights/BedIcon';
import OccupancyIcon from '../../icons/highlights/OccupancyIcon';
import ViewIcon from '../../icons/highlights/ViewIcon';
import SizeIcon from '../../icons/highlights/SizeIcon';
import ServiceIcon from '../../icons/highlights/ServiceIcon';

import { getPhotoThumb, getLangValue } from '../../../utils/Generic';

const getIcon = (iconName) => {
  switch (iconName) {
    case 'highlight-bed-icon':
      return <BedIcon />;
    case 'highlight-view-icon':
      return <ViewIcon />;
    case 'highlight-service-icon':
      return <ServiceIcon />;
    case 'highlight-size-icon':
      return <SizeIcon />;
    case 'highlight-occupancy-icon':
    default:
      return <OccupancyIcon />;
  }
};

/*
  Sorts the icons with known names so that they appear in the expected order.
  In case it finds unknown icons, they get put to the bottom
 */
const sortHighlights = (highlights) => {
  const sortedIconNames = [
    'highlight-bed-icon',
    'highlight-occupancy-icon',
    'highlight-service-icon',
    'highlight-size-icon',
    'highlight-view-icon',
  ];
  return highlights.sort((a, b) => {
    let indexA = sortedIconNames.indexOf(a.icon);
    let indexB = sortedIconNames.indexOf(b.icon);
    if (indexA === -1) {
      indexA = 100;
    }
    if (indexB === -1) {
      indexB = 100;
    }
    return indexA - indexB;
  });
};

const renderImage = ({ icon, image }) => {
  let content = null;
  if (icon) {
    content = getIcon(icon);
  } else if (image && image.url) {
    content = getPhotoThumb({ url: image.url, width: 100 });
  }
  if (!content) {
    return null;
  }
  return (
    <figure className="w-8 flex-shrink-0">
      {content}
    </figure>
  );
};

const RoomHighlights = (props) => {
  const { highlights, lang } = props;
  if (highlights.length === 0) {
    return null;
  }
  const sortedHighlights = sortHighlights(highlights);
  if (highlights.length === 0) {
    return null;
  }
  return (
    <div className="border border-teal-500 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
      {sortedHighlights.map((highlight, index) => (
        <div className="flex p-4 font-extrabold text-xs" key={`highlight_${index}`}>
          {renderImage(highlight)}
          <div className="ml-4 capitalize">
            <div className="text-gray-500">{getLangValue(highlight, lang, 'category')}</div>
            <div>{getLangValue(highlight, lang, 'name')}</div>
          </div>
          {index < highlights.length - 1 && <p className="separator"/>}
        </div>
      ))}
    </div>
  );
};

RoomHighlights.propTypes = {
  highlights: PropTypes.arrayOf(
    PropTypes.shape({
      category: PropTypes.string,
      name: PropTypes.string,
      icon: PropTypes.string,
      image: PropTypes.object,
    }),
  ).isRequired,
  lang: PropTypes.string.isRequired,
};

export default RoomHighlights;
